import { supabase } from './supabase';
import { noderr, DatabaseError } from '../../shared/utils/errorHandler';
import type { ReleaseContactLink } from '../../shared/types/entities';

const releaseContactSelect = `
  *,
  contact:client_contacts (
    id,
    client_id,
    first_name,
    last_name,
    email,
    phone,
    title,
    department,
    is_primary,
    notes,
    created_at,
    updated_at,
    client:contacts (
      id,
      name
    )
  )
`;

class ReleaseContactsService {
  async listByRelease(releaseId: string): Promise<ReleaseContactLink[]> {
    const [error, result] = await noderr(
      supabase
        .from('release_contacts')
        .select(releaseContactSelect)
        .eq('release_id', releaseId)
        .order('created_at', { ascending: true })
    );

    if (error) {
      throw new DatabaseError(`Failed to load release contacts: ${error.message}`);
    }

    return ((result as any)?.data || []) as ReleaseContactLink[];
  }

  async linkContact(releaseId: string, clientContactId: string, attributes: { role?: string; notes?: string } = {}): Promise<ReleaseContactLink> {
    const payload = {
      release_id: releaseId,
      client_contact_id: clientContactId,
      role: attributes.role ?? null,
      notes: attributes.notes ?? null,
    };

    const [error, result] = await noderr(
      supabase
        .from('release_contacts')
        .insert(payload)
        .select(releaseContactSelect)
        .single()
    );

    if (error) {
      if ((error as any)?.code === '23505') {
        throw new DatabaseError('Contact is already linked to this release.');
      }
      throw new DatabaseError(`Failed to link contact to release: ${error.message}`);
    }

    return (result as any)?.data as ReleaseContactLink;
  }

  async unlinkContact(linkId: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('release_contacts')
        .delete()
        .eq('id', linkId)
    );

    if (error) {
      throw new DatabaseError(`Failed to unlink contact: ${error.message}`);
    }
  }
}

export const releaseContactsService = new ReleaseContactsService();
